import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { 
  ArrowLeft, 
  Target, 
  CheckCircle2,
  Calendar,
  Clock,
  Building2,
  ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import "./CaseStudy.css";


// Full case study data
const caseStudyData = {
  "personal-loans": {
    category: "Personal Loans",
    client: "Top 10 National Lender",
    industry: "Consumer Finance",
    duration: "6 months",
    heroImage: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1200&h=600&fit=crop",
    summary: "A leading national personal loan provider partnered with IntentLogic to identify high-intent borrowers and optimize their customer acquisition funnel, resulting in significant improvements across all key metrics.",
    challenge: {
      title: "The Challenge",
      description: "The client was facing increasing competition in the personal loan market, with rising customer acquisition costs and declining conversion rates. Their existing targeting approach relied heavily on broad demographic data, resulting in wasted ad spend on low-intent audiences.",
      points: [
        "Customer acquisition costs had increased 34% year-over-year",
        "Conversion rates from application to funded loan were declining",
        "Difficulty identifying consumers actively in-market for personal loans",
        "Limited visibility into cross-channel customer journey",
        "Compliance requirements restricted traditional targeting methods"
      ]
    },
    solution: {
      title: "Our Approach",
      description: "IntentLogic deployed our IRIS platform to identify consumers demonstrating real-time intent signals for personal loan products, combining behavioral, transactional, and contextual data to build high-propensity audiences.",
      points: [
        "Deployed intent-based audience segments targeting active loan researchers",
        "Integrated 245M+ consumer profiles with real-time behavioral signals",
        "Built custom propensity models predicting likelihood to fund",
        "Implemented multi-touch attribution across all channels",
        "Created compliant targeting strategies within regulatory guidelines"
      ]
    },
    results: {
      title: "The Results",
      metrics: [
        { value: "62%", label: "Increase in Funded Loan Volume", description: "Compared to previous 6-month period" },
        { value: "41%", label: "Reduction in Cost-Per-Acquisition", description: "Lower spend to acquire each funded loan" },
        { value: "$2,400", label: "Higher Average Loan Size", description: "Better quality borrowers with higher credit needs" },
        { value: "2.3x", label: "Improvement in ROAS", description: "Return on advertising spend" }
      ],
      timeline: [
        { month: "Month 1", achievement: "Platform integration and baseline measurement" },
        { month: "Month 2", achievement: "Initial audience deployment, 18% lift in applications" },
        { month: "Month 3", achievement: "Optimization phase, CPA reduction begins" },
        { month: "Month 4", achievement: "Scale-up with proven audiences" },
        { month: "Month 5", achievement: "Full funnel optimization implemented" },
        { month: "Month 6", achievement: "Peak performance achieved, results validated" }
      ]
    },
    testimonial: {
      quote: "IntentLogic transformed our approach to customer acquisition. Their ability to identify consumers actively researching personal loans allowed us to reach the right people at the right time, dramatically improving our efficiency.",
      author: "VP of Digital Marketing",
      company: "Top 10 National Lender"
    },
    methodology: {
      title: "Methodology Deep Dive",
      sections: [
        {
          title: "Intent Signal Identification",
          content: "We identified 47 distinct behavioral signals indicating personal loan intent, including comparison shopping behavior, rate calculator usage, credit check activity, and financial content consumption patterns."
        },
        {
          title: "Propensity Modeling",
          content: "Our machine learning models analyzed historical conversion data to identify the characteristics most predictive of loan funding, creating custom scoring algorithms specific to the client's product offerings."
        },
        {
          title: "Multi-Channel Activation",
          content: "Audiences were activated across programmatic display, social media, email, and direct mail channels, with consistent measurement and attribution across all touchpoints."
        },
        {
          title: "Continuous Optimization",
          content: "Weekly performance reviews and model retraining ensured audiences remained fresh and targeting continued to improve throughout the engagement."
        }
      ]
    }
  },
  "credit-cards": {
    category: "Credit Cards",
    client: "Major Credit Card Issuer",
    industry: "Financial Services",
    duration: "4 months",
    heroImage: "https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?w=1200&h=600&fit=crop",
    summary: "A leading credit card issuer leveraged IntentLogic's predictive intelligence to target consumers with strong credit profiles showing intent signals for premium rewards cards, achieving exceptional approval rates and cardholder engagement.",
    challenge: {
      title: "The Challenge",
      description: "The issuer needed to grow their premium rewards card portfolio while maintaining strict credit quality standards. Traditional targeting methods resulted in high decline rates and poor unit economics.",
      points: [
        "Approval rates were below industry benchmarks at 52%",
        "High-value cardholders were difficult to identify at scale",
        "First-year attrition was impacting lifetime value projections",
        "Competitive pressure from fintech challengers",
        "Need to balance growth with credit risk management"
      ]
    },
    solution: {
      title: "Our Approach",
      description: "IntentLogic built custom audience segments combining credit quality indicators with behavioral intent signals, identifying consumers most likely to apply, be approved, and become active cardholders.",
      points: [
        "Created credit-qualified intent audiences using permissioned data",
        "Identified signals indicating premium card shopping behavior",
        "Built predictive models for first-year spend potential",
        "Deployed cross-channel campaigns with consistent messaging",
        "Implemented real-time optimization based on approval feedback"
      ]
    },
    results: {
      title: "The Results",
      metrics: [
        { value: "78%", label: "Approval Rate", description: "vs. 52% benchmark with previous targeting" },
        { value: "34%", label: "Higher First-Year Spend", description: "Per cardholder compared to control group" },
        { value: "2.8x", label: "ROAS Within 90 Days", description: "Faster path to profitability" },
        { value: "23%", label: "Lower First-Year Attrition", description: "Better long-term cardholder retention" }
      ],
      timeline: [
        { month: "Month 1", achievement: "Audience development and compliance review" },
        { month: "Month 2", achievement: "Pilot launch with 78% approval rate achieved" },
        { month: "Month 3", achievement: "Scale across additional channels" },
        { month: "Month 4", achievement: "Full deployment and optimization" }
      ]
    },
    testimonial: {
      quote: "The precision of IntentLogic's targeting was remarkable. We significantly improved approval rates while actually increasing cardholder quality and engagement. This is exactly the kind of intelligent growth we needed.",
      author: "Head of Card Acquisition",
      company: "Major Credit Card Issuer"
    },
    methodology: {
      title: "Methodology Deep Dive",
      sections: [
        {
          title: "Credit-Intent Fusion",
          content: "We combined anonymized credit indicators with real-time behavioral signals to identify consumers who both qualify for premium cards and are actively considering new card products."
        },
        {
          title: "Spend Potential Modeling",
          content: "Our models predicted likely first-year spend based on lifestyle indicators, travel behavior, and shopping patterns, helping prioritize the highest-value prospects."
        },
        {
          title: "Competitive Conquest",
          content: "We identified consumers showing dissatisfaction signals with current cards, including reward comparison behavior and customer service search patterns."
        },
        {
          title: "Approval Feedback Loop",
          content: "Real-time approval/decline data was fed back into our models, allowing continuous refinement of targeting criteria to optimize approval rates."
        }
      ]
    }
  },
  "credit-score-services": {
    category: "Credit Score Services",
    client: "Leading Credit Monitoring Platform",
    industry: "Financial Technology",
    duration: "5 months",
    heroImage: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=600&fit=crop",
    summary: "A leading credit monitoring and improvement platform partnered with IntentLogic to acquire credit-conscious consumers through predictive audience targeting, dramatically improving conversion rates and customer lifetime value.",
    challenge: {
      title: "The Challenge",
      description: "The client offered a freemium credit monitoring service but struggled to convert free users to paid subscriptions. Customer acquisition costs were high, and trial-to-paid conversion rates lagged behind targets.",
      points: [
        "Trial-to-paid conversion rate stuck at 8%",
        "High customer acquisition costs eroding unit economics",
        "Difficulty distinguishing serious credit improvers from casual browsers",
        "Competitive market with multiple free alternatives",
        "Need to improve 12-month customer lifetime value"
      ]
    },
    solution: {
      title: "Our Approach",
      description: "IntentLogic developed audience segments targeting consumers demonstrating genuine credit improvement intent, focusing on those most likely to value and pay for premium features.",
      points: [
        "Identified behavioral signals indicating serious credit improvement intent",
        "Built propensity models predicting subscription conversion likelihood",
        "Created lookalike audiences from highest-LTV existing customers",
        "Deployed sequential messaging aligned to credit improvement journey",
        "Implemented predictive churn models to improve retention"
      ]
    },
    results: {
      title: "The Results",
      metrics: [
        { value: "156%", label: "Lift in Trial-to-Paid Conversion", description: "From 8% to 20.5% conversion rate" },
        { value: "47%", label: "Lower Customer Acquisition Cost", description: "More efficient path to paid subscribers" },
        { value: "3.2x", label: "Increase in 12-Month LTV", description: "Higher-quality, longer-retained customers" },
        { value: "89%", label: "12-Month Retention Rate", description: "Vs. 71% with previous acquisition methods" }
      ],
      timeline: [
        { month: "Month 1", achievement: "Customer analysis and intent signal mapping" },
        { month: "Month 2", achievement: "Model development and audience creation" },
        { month: "Month 3", achievement: "Pilot campaign launch, early conversion lift observed" },
        { month: "Month 4", achievement: "Optimization and scale-up" },
        { month: "Month 5", achievement: "Full deployment with retention focus" }
      ]
    },
    testimonial: {
      quote: "IntentLogic helped us find the customers who truly needed our service, not just those casually curious about their credit score. The improvement in conversion and retention has transformed our business economics.",
      author: "Chief Marketing Officer",
      company: "Leading Credit Monitoring Platform"
    },
    methodology: {
      title: "Methodology Deep Dive",
      sections: [
        {
          title: "Intent Classification",
          content: "We categorized credit-related intent signals into tiers: casual curiosity, active monitoring interest, serious improvement intent, and urgent credit need. Targeting focused on the highest-intent segments."
        },
        {
          title: "LTV Prediction",
          content: "Our models analyzed behavioral patterns that correlated with long-term subscription retention, allowing us to prioritize acquisition of high-LTV prospects."
        },
        {
          title: "Journey-Based Messaging",
          content: "We mapped the credit improvement journey and developed sequential messaging that educated and converted prospects at each stage of their decision process."
        },
        {
          title: "Retention Optimization",
          content: "Predictive churn models identified at-risk subscribers early, enabling proactive retention campaigns that significantly improved 12-month retention rates."
        }
      ]
    }
  }
};

// Main Case Study Component
const CaseStudyPage = () => {
  const { slug } = useParams();
  const study = caseStudyData[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!study) {
    return (
      <Layout>
        <div className="case-study-page">
          <div className="case-study-not-found">
            <h1>Case Study Not Found</h1>
            <p>The case study you're looking for doesn't exist.</p>
            <Link to="/#results" className="btn-primary">
              <ArrowLeft size={18} />
              Back to Case Studies
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="case-study-page">
        <SEO 
          title={`${study.category} Case Study - Marketing Results`}
          description={`${study.summary} Learn how IntentLogic delivered measurable results for ${study.client} in the ${study.industry} industry.`}
          keywords={`case study, ${study.category.toLowerCase()}, marketing results, ${study.industry.toLowerCase()}, performance marketing, campaign success`}
          canonical={`/case-study/${slug}`}
        />
        
        {/* Hero Section */}
        <section className="case-study-hero">
          <div className="case-study-hero-content">
            <Link to="/#results" className="back-link">
              <ArrowLeft size={18} />
              Back to Case Studies
            </Link>
            <span className="case-study-category">{study.category}</span>
            <h1>{study.category} Case Study</h1>
            <p className="case-study-summary">{study.summary}</p>
            <div className="case-study-meta">
              <div className="meta-item">
                <Building2 size={18} />
                <span>{study.client}</span>
              </div>
              <div className="meta-item">
                <Target size={18} />
                <span>{study.industry}</span>
              </div>
              <div className="meta-item">
                <Clock size={18} />
                <span>{study.duration}</span>
              </div>
            </div>
          </div>
          <div className="case-study-hero-image">
            <img src={study.heroImage} alt={study.category} />
          </div>
        </section>

        {/* Results Overview */}
        <section className="case-study-results-overview">
          <h2>Key Results</h2>
          <div className="results-grid">
            {study.results.metrics.map((metric, index) => (
              <div key={index} className="result-card">
                <span className="result-value">{metric.value}</span>
                <span className="result-label">{metric.label}</span>
                <span className="result-description">{metric.description}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Challenge Section */}
        <section className="case-study-section">
          <div className="section-content">
            <div className="section-text">
              <h2>{study.challenge.title}</h2>
              <p>{study.challenge.description}</p>
              <ul className="challenge-list">
                {study.challenge.points.map((point, index) => (
                  <li key={index}>
                    <span className="challenge-icon"><Target size={18} /></span>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Solution Section */}
        <section className="case-study-section solution-section">
          <div className="section-content">
            <div className="section-text">
              <h2>{study.solution.title}</h2>
              <p>{study.solution.description}</p>
              <ul className="solution-list">
                {study.solution.points.map((point, index) => (
                  <li key={index}>
                    <span className="solution-icon"><CheckCircle2 size={18} /></span>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Timeline Section */}
        <section className="case-study-timeline">
          <h2>Implementation Timeline</h2>
          <div className="timeline-container">
            {study.results.timeline.map((item, index) => (
              <div key={index} className="timeline-item">
                <div className="timeline-marker">
                  <Calendar size={18} />
                </div>
                <div className="timeline-content">
                  <span className="timeline-month">{item.month}</span>
                  <p>{item.achievement}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Methodology Section */}
        <section className="case-study-methodology">
          <h2>{study.methodology.title}</h2>
          <div className="methodology-grid">
            {study.methodology.sections.map((section, index) => (
              <div key={index} className="methodology-card">
                <h3>{section.title}</h3>
                <p>{section.content}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Testimonial Section */}
        <section className="case-study-testimonial">
          <div className="testimonial-content">
            <blockquote>"{study.testimonial.quote}"</blockquote>
            <div className="testimonial-author">
              <span className="author-name">{study.testimonial.author}</span>
              <span className="author-company">{study.testimonial.company}</span>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="case-study-cta">
          <h2>Ready to Achieve Similar Results?</h2>
          <p>Let's discuss how IntentLogic can help drive measurable outcomes for your business.</p>
          <Button className="btn-primary" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            Schedule Consultation
            <ChevronRight size={18} />
          </Button>
        </section>

        {/* Other Case Studies */}
        <section className="other-case-studies">
          <h2>Explore More Case Studies</h2>
          <div className="other-studies-grid">
            {Object.entries(caseStudyData)
              .filter(([key]) => key !== slug)
              .map(([key, otherStudy]) => (
                <Link key={key} to={`/case-study/${key}`} className="other-study-card">
                  <img src={otherStudy.heroImage} alt={otherStudy.category} />
                  <div className="other-study-content">
                    <span className="other-study-category">{otherStudy.category}</span>
                    <p>{otherStudy.client}</p>
                    <span className="view-link">
                      View Case Study <ChevronRight size={16} />
                    </span>
                  </div>
                </Link>
              ))}
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default CaseStudyPage;
